import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaUser, 
  FaIdCard, 
  FaPhone, 
  FaEnvelope,
  FaCreditCard,
  FaShieldAlt,
  FaCheckCircle,
  FaChair
} from 'react-icons/fa';
import { MdEventSeat, MdPayment } from 'react-icons/md';
import logo from '../../components/assets/logos/logo2.PNG';

const BusSeatSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bus = location.state?.bus || {
    id: 1,
    name: "Volvo AC Sleeper",
    operator: "Green Line Travels",
    departure: "08:00 AM",
    arrival: "02:30 PM",
    price: 1200,
    type: "SLEEPER"
  };

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerDetails, setPassengerDetails] = useState([{
    name: '',
    age: '',
    gender: 'Male',
    idNumber: '',
    phone: '',
    email: ''
  }]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Seat Selection, 2: Passenger Details, 3: Payment
  const [selectedPayment, setSelectedPayment] = useState('');

  // Seat layout for sleeper bus
  const seatLayout = [
    ['A1', 'A2', null, 'A3', 'A4'],
    ['B1', 'B2', null, 'B3', 'B4'],
    ['C1', 'C2', null, 'C3', 'C4'],
    ['D1', 'D2', null, 'D3', 'D4'],
    ['E1', 'E2', null, 'E3', 'E4'],
    ['F1', 'F2', null, 'F3', 'F4'],
  ];

  // Booked seats (in real app, this would come from API)
  const bookedSeats = ['A1', 'B3', 'C4', 'D2', 'F1'];

  const handleSeatClick = (seat) => {
    if (bookedSeats.includes(seat)) return;
    
    if (selectedSeats.includes(seat)) {
      setSelectedSeats(selectedSeats.filter(s => s !== seat));
    } else {
      if (selectedSeats.length < 5) { // Max 5 seats per booking
        setSelectedSeats([...selectedSeats, seat]);
      } else {
        alert('Maximum 5 seats per booking');
      }
    }
  };

  const handlePassengerChange = (index, field, value) => {
    const updatedPassengers = [...passengerDetails];
    updatedPassengers[index] = {
      ...updatedPassengers[index],
      [field]: value
    };
    setPassengerDetails(updatedPassengers);
  };

  const addPassenger = () => {
    if (passengerDetails.length < selectedSeats.length) {
      setPassengerDetails([
        ...passengerDetails,
        { name: '', age: '', gender: 'Male', idNumber: '', phone: '', email: '' }
      ]);
    }
  };

  const calculateTotal = () => {
    return selectedSeats.length * bus.price;
  };

  const handleProceed = () => {
    if (currentStep === 1) {
      if (selectedSeats.length === 0) {
        alert('Please select at least one seat');
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Validate passenger details
      const isValid = passengerDetails.slice(0, selectedSeats.length).every(passenger => 
        passenger.name && passenger.age && passenger.phone
      );
      if (!isValid) {
        alert('Please fill all required passenger details');
        return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      if (!selectedPayment) {
        alert('Please select a payment method');
        return;
      }
      // In real app, this would process payment
      alert('Booking confirmed! Redirecting to bookings page...');
      navigate('/mybookings');
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
     fontFamily: "'Noto Serif Devanagari', serif",

    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '20px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      textDecoration: 'none',
      color: 'white'
    },
    logoImage: {
      height: '50px',
      width: 'auto',
      objectFit: 'contain',
      borderRadius: '8px'
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      letterSpacing: '0.5px'
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '30px 20px'
    },
    progressBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '40px',
      position: 'relative'
    },
    progressStep: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      zIndex: 2
    },
    stepCircle: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.2rem',
      fontWeight: 'bold',
      transition: 'all 0.3s ease'
    },
    stepCircleActive: {
      background: '#667eea',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
    },
    stepCircleInactive: {
      background: '#e0e0e0',
      color: '#666'
    },
    stepLabel: {
      fontSize: '0.9rem',
      fontWeight: '500',
      color: '#666'
    },
    stepLabelActive: {
      color: '#667eea',
      fontWeight: '600'
    },
    progressLine: {
      position: 'absolute',
      top: '25px',
      left: '10%',
      right: '10%',
      height: '4px',
      background: '#e0e0e0',
      zIndex: 1
    },
    progressLineFill: {
      height: '100%',
      background: '#667eea',
      transition: 'width 0.3s ease',
      width: currentStep === 1 ? '0%' : currentStep === 2 ? '50%' : '100%'
    },
    bookingCard: {
      background: 'white',
      borderRadius: '20px',
      padding: '30px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      marginBottom: '30px'
    },
    cardTitle: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#333',
      marginBottom: '25px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    busInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '12px',
      marginBottom: '30px'
    },
    busName: {
      fontSize: '1.3rem',
      fontWeight: '600',
      color: '#333'
    },
    busDetails: {
      display: 'flex',
      gap: '30px',
      color: '#666'
    },
    busPrice: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#667eea'
    },
    seatSelection: {
      marginTop: '30px'
    },
    seatLegend: {
      display: 'flex',
      justifyContent: 'center',
      gap: '30px',
      marginBottom: '30px'
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.9rem',
      color: '#666'
    },
    legendBox: {
      width: '24px',
      height: '24px',
      borderRadius: '6px',
      border: '2px solid'
    },
    busDiagram: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '15px',
      marginBottom: '40px'
    },
    busBody: {
      width: '100%',
      maxWidth: '500px',
      padding: '30px',
      background: '#f8f9fa',
      borderRadius: '15px',
      border: '2px solid #e0e0e0'
    },
    seatRow: {
      display: 'flex',
      justifyContent: 'center',
      gap: '15px',
      marginBottom: '15px'
    },
    seat: {
      width: '50px',
      height: '50px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid',
      fontSize: '0.9rem'
    },
    seatAvailable: {
      background: 'white',
      borderColor: '#4CAF50',
      color: '#4CAF50'
    },
    seatSelected: {
      background: '#667eea',
      borderColor: '#667eea',
      color: 'white',
      transform: 'scale(1.1)'
    },
    seatBooked: {
      background: '#f44336',
      borderColor: '#f44336',
      color: 'white',
      cursor: 'not-allowed'
    },
    seatEmpty: {
      visibility: 'hidden'
    },
    seatAisle: {
      width: '100px'
    },
    passengerForm: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    formLabel: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#555',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    formInput: {
      padding: '12px 15px',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    formInputFocus: {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)'
    },
    selectInput: {
      padding: '12px 15px',
      border: '2px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      outline: 'none'
    },
    addPassengerBtn: {
      background: '#f0f4ff',
      border: '2px dashed #667eea',
      color: '#667eea',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      marginTop: '20px'
    },
    paymentMethods: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginTop: '20px'
    },
    paymentMethod: {
      border: '2px solid #e0e0e0',
      borderRadius: '12px',
      padding: '20px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    },
    paymentMethodSelected: {
      borderColor: '#667eea',
      background: '#f0f4ff',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.1)'
    },
    paymentIcon: {
      fontSize: '2rem',
      color: '#667eea'
    },
    paymentInfo: {
      flex: 1
    },
    paymentTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#333',
      marginBottom: '5px'
    },
    paymentDesc: {
      fontSize: '0.9rem',
      color: '#666'
    },
    bookingSummary: {
      background: '#f8f9fa',
      borderRadius: '15px',
      padding: '25px',
      marginTop: '30px'
    },
    summaryTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#333',
      marginBottom: '20px'
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
      fontSize: '1rem',
      color: '#666'
    },
    summaryTotal: {
      fontSize: '1.3rem',
      fontWeight: '700',
      color: '#667eea',
      paddingTop: '15px',
      borderTop: '2px solid #e0e0e0',
      marginTop: '15px'
    },
    actionButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '40px',
      paddingTop: '30px',
      borderTop: '1px solid #e0e0e0'
    },
    backButton: {
      background: '#f8f9fa',
      border: '2px solid #e0e0e0',
      color: '#666',
      padding: '14px 30px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease'
    },
    proceedButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '14px 40px',
      borderRadius: '8px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
    },
    footer: {
      background: '#2d3748',
      color: 'white',
      padding: '40px 20px',
      marginTop: '60px'
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px'
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    footerTitle: {
      fontSize: '1.2rem',
      fontWeight: '600',
      marginBottom: '10px'
    },
    footerLink: {
      color: '#cbd5e0',
      textDecoration: 'none',
      fontSize: '0.9rem',
      transition: 'color 0.3s ease'
    },
    footerLinkHover: {
      color: 'white'
    },
    copyright: {
      textAlign: 'center',
      padding: '20px',
      borderTop: '1px solid #4a5568',
      marginTop: '40px',
      fontSize: '0.9rem',
      color: '#a0aec0'
    }
  };

  const getSeatStyle = (seat) => {
    if (!seat) return styles.seatEmpty;
    if (bookedSeats.includes(seat)) return { ...styles.seat, ...styles.seatBooked };
    if (selectedSeats.includes(seat)) return { ...styles.seat, ...styles.seatSelected };
    return { ...styles.seat, ...styles.seatAvailable };
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
            <Link to="/user-bus-finder" style={styles.logoLink}>
              <FaArrowLeft style={{ fontSize: '1.2rem' }} />
              <img src={logo} alt="Bus Management System Logo" style={styles.logoImage} />
              <span style={styles.logoText}>Book Bus</span>
            </Link>
          </div>
          <div style={{ color: 'white', fontSize: '0.9rem' }}>
            Secure Booking • 24/7 Support
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={styles.mainContent}>
        {/* Progress Bar */}
        <div style={styles.progressBar}>
          <div style={styles.progressLine}>
            <div style={styles.progressLineFill}></div>
          </div>
          {['Select Seats', 'Passenger Details', 'Payment'].map((step, index) => (
            <div key={index} style={styles.progressStep}>
              <div style={{
                ...styles.stepCircle,
                ...(currentStep > index + 1 ? styles.stepCircleActive : 
                     currentStep === index + 1 ? styles.stepCircleActive : styles.stepCircleInactive)
              }}>
                {currentStep > index + 1 ? <FaCheckCircle /> : index + 1}
              </div>
              <div style={{
                ...styles.stepLabel,
                ...(currentStep >= index + 1 ? styles.stepLabelActive : {})
              }}>
                {step}
              </div>
            </div>
          ))}
        </div>

        {/* Booking Card */}
        <div style={styles.bookingCard}>
          {/* Bus Info */}
          <div style={styles.busInfo}>
            <div>
              <div style={styles.busName}>{bus.name}</div>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>{bus.operator}</div>
            </div>
            <div style={styles.busDetails}>
              <div>
                <div style={{ fontWeight: '600' }}>Departure</div>
                <div>{bus.departure}</div>
              </div>
              <div>
                <div style={{ fontWeight: '600' }}>Arrival</div>
                <div>{bus.arrival}</div>
              </div>
              <div>
                <div style={{ fontWeight: '600' }}>Bus Type</div>
                <div>{bus.type}</div>
              </div>
            </div>
            <div style={styles.busPrice}>₹{bus.price}/seat</div>
          </div>

          {/* Step 1: Seat Selection */}
          {currentStep === 1 && (
            <div style={styles.seatSelection}>
              <h3 style={styles.cardTitle}><MdEventSeat /> Select Your Seats</h3>
              
              <div style={styles.seatLegend}>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#4CAF50', borderColor: '#4CAF50' }}></div>
                  <span>Available</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#667eea', borderColor: '#667eea' }}></div>
                  <span>Selected</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{ ...styles.legendBox, background: '#f44336', borderColor: '#f44336' }}></div>
                  <span>Booked</span>
                </div>
              </div>

              <div style={styles.busDiagram}>
                <div style={{ 
                  width: '100%', 
                  maxWidth: '500px', 
                  textAlign: 'center', 
                  padding: '10px', 
                  background: '#667eea', 
                  color: 'white', 
                  borderRadius: '8px',
                  fontWeight: '600'
                }}>
                  <FaChair style={{ marginRight: '10px' }} /> Driver's Cabin
                </div>
                <div style={styles.busBody}>
                  {seatLayout.map((row, rowIndex) => (
                    <div key={rowIndex} style={styles.seatRow}>
                      {row.map((seat, seatIndex) => (
                        seat === null ? (
                          <div key={seatIndex} style={styles.seatAisle}></div>
                        ) : (
                          <div
                            key={seatIndex}
                            style={getSeatStyle(seat)}
                            onClick={() => handleSeatClick(seat)}
                            title={bookedSeats.includes(seat) ? 'Already Booked' : seat}
                          >
                            {seat}
                          </div>
                        )
                      ))}
                    </div>
                  ))}
                </div>
                <div style={{ 
                  width: '100%', 
                  maxWidth: '500px', 
                  textAlign: 'center', 
                  padding: '10px', 
                  background: '#e0e0e0', 
                  borderRadius: '8px',
                  fontWeight: '600'
                }}>
                  Exit
                </div>
              </div>

              <div style={styles.bookingSummary}>
                <div style={styles.summaryTitle}>Booking Summary</div>
                <div style={styles.summaryRow}>
                  <span>Selected Seats:</span>
                  <span>{selectedSeats.length > 0 ? selectedSeats.join(', ') : 'None'}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Price per Seat:</span>
                  <span>₹{bus.price}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span>Total Seats:</span>
                  <span>{selectedSeats.length}</span>
                </div>
                <div style={styles.summaryTotal}>
                  <span>Total Amount:</span>
                  <span>₹{calculateTotal()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Passenger Details */}
          {currentStep === 2 && (
            <div>
              <h3 style={styles.cardTitle}><FaUser /> Passenger Details</h3>
              <p style={{ color: '#666', marginBottom: '25px' }}>
                Please enter details for {selectedSeats.length} passenger(s)
              </p>

              <div style={styles.passengerForm}>
                {passengerDetails.slice(0, selectedSeats.length).map((passenger, index) => (
                  <div key={index} style={{ marginBottom: '30px' }}>
                    <h4 style={{ color: '#667eea', marginBottom: '15px' }}>
                      Passenger {index + 1} - Seat {selectedSeats[index]}
                    </h4>
                    <div style={styles.passengerForm}>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>
                          <FaUser /> Full Name *
                        </label>
                        <input
                          type="text"
                          value={passenger.name}
                          onChange={(e) => handlePassengerChange(index, 'name', e.target.value)}
                          placeholder="Enter full name"
                          style={styles.formInput}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Age *</label>
                        <input
                          type="number"
                          value={passenger.age}
                          onChange={(e) => handlePassengerChange(index, 'age', e.target.value)}
                          placeholder="Age"
                          min="1"
                          max="100"
                          style={styles.formInput}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>Gender</label>
                        <select
                          value={passenger.gender}
                          onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                          style={styles.selectInput}
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>
                          <FaIdCard /> ID Number
                        </label>
                        <input
                          type="text"
                          value={passenger.idNumber}
                          onChange={(e) => handlePassengerChange(index, 'idNumber', e.target.value)}
                          placeholder="Aadhaar/Passport Number"
                          style={styles.formInput}
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>
                          <FaPhone /> Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={passenger.phone}
                          onChange={(e) => handlePassengerChange(index, 'phone', e.target.value)}
                          placeholder="10-digit mobile number"
                          style={styles.formInput}
                          required
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.formLabel}>
                          <FaEnvelope /> Email Address
                        </label>
                        <input
                          type="email"
                          value={passenger.email}
                          onChange={(e) => handlePassengerChange(index, 'email', e.target.value)}
                          placeholder="email@example.com"
                          style={styles.formInput}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {passengerDetails.length < selectedSeats.length && (
                <button
                  type="button"
                  style={styles.addPassengerBtn}
                  onClick={addPassenger}
                >
                  + Add Another Passenger
                </button>
              )}
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div>
              <h3 style={styles.cardTitle}><MdPayment /> Select Payment Method</h3>
              
              <div style={styles.paymentMethods}>
                <div
                  style={{
                    ...styles.paymentMethod,
                    ...(selectedPayment === 'card' ? styles.paymentMethodSelected : {})
                  }}
                  onClick={() => setSelectedPayment('card')}
                >
                  <FaCreditCard style={styles.paymentIcon} />
                  <div style={styles.paymentInfo}>
                    <div style={styles.paymentTitle}>Credit/Debit Card</div>
                    <div style={styles.paymentDesc}>Pay using Visa, MasterCard, RuPay</div>
                  </div>
                  {selectedPayment === 'card' && <FaCheckCircle style={{ color: '#4CAF50' }} />}
                </div>

                <div
                  style={{
                    ...styles.paymentMethod,
                    ...(selectedPayment === 'upi' ? styles.paymentMethodSelected : {})
                  }}
                  onClick={() => setSelectedPayment('upi')}
                >
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 'bold', 
                    color: '#667eea',
                    background: '#f0f4ff',
                    padding: '10px',
                    borderRadius: '8px'
                  }}>
                    UPI
                  </div>
                  <div style={styles.paymentInfo}>
                    <div style={styles.paymentTitle}>UPI Payment</div>
                    <div style={styles.paymentDesc}>Google Pay, PhonePe, Paytm, BHIM</div>
                  </div>
                  {selectedPayment === 'upi' && <FaCheckCircle style={{ color: '#4CAF50' }} />}
                </div>

                <div
                  style={{
                    ...styles.paymentMethod,
                    ...(selectedPayment === 'netbanking' ? styles.paymentMethodSelected : {})
                  }}
                  onClick={() => setSelectedPayment('netbanking')}
                >
                  <FaShieldAlt style={styles.paymentIcon} />
                  <div style={styles.paymentInfo}>
                    <div style={styles.paymentTitle}>Net Banking</div>
                    <div style={styles.paymentDesc}>All major Indian banks</div>
                  </div>
                  {selectedPayment === 'netbanking' && <FaCheckCircle style={{ color: '#4CAF50' }} />}
                </div>

                <div
                  style={{
                    ...styles.paymentMethod,
                    ...(selectedPayment === 'wallet' ? styles.paymentMethodSelected : {})
                  }}
                  onClick={() => setSelectedPayment('wallet')}
                >
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: 'bold', 
                    color: '#667eea',
                    background: '#f0f4ff',
                    padding: '10px',
                    borderRadius: '8px'
                  }}>
                    💰
                  </div>
                  <div style={styles.paymentInfo}>
                    <div style={styles.paymentTitle}>Wallet</div>
                    <div style={styles.paymentDesc}>Paytm Wallet, Amazon Pay</div>
                  </div>
                  {selectedPayment === 'wallet' && <FaCheckCircle style={{ color: '#4CAF50' }} />}
                </div>
              </div>

              {selectedPayment && (
                <div style={styles.bookingSummary}>
                  <div style={styles.summaryTitle}>Payment Summary</div>
                  <div style={styles.summaryRow}>
                    <span>Bus Fare:</span>
                    <span>₹{bus.price} × {selectedSeats.length}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>Service Fee:</span>
                    <span>₹{selectedSeats.length * 20}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>GST (18%):</span>
                    <span>₹{Math.round(calculateTotal() * 0.18)}</span>
                  </div>
                  <div style={styles.summaryTotal}>
                    <span>Total Payable:</span>
                    <span>₹{calculateTotal() + (selectedSeats.length * 20) + Math.round(calculateTotal() * 0.18)}</span>
                  </div>
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    background: '#e8f5e9', 
                    borderRadius: '8px',
                    color: '#2e7d32'
                  }}>
                    <FaShieldAlt style={{ marginRight: '10px' }} />
                    Your payment is secured with 256-bit SSL encryption
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            {currentStep > 1 && (
              <button
                style={styles.backButton}
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                <FaArrowLeft /> Back
              </button>
            )}
            <button
              style={{
                ...styles.proceedButton,
                ...(currentStep === 1 && selectedSeats.length === 0 ? { opacity: 0.6, cursor: 'not-allowed' } : {})
              }}
              onClick={handleProceed}
              disabled={currentStep === 1 && selectedSeats.length === 0}
            >
              {currentStep === 3 ? 'Confirm & Pay' : 'Proceed'}
              {currentStep < 3 && <FaArrowLeft style={{ transform: 'rotate(180deg)' }} />}
            </button>
          </div>
        </div>

        {/* Booking Policy */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '25px',
          marginTop: '30px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)'
        }}>
          <h4 style={{ color: '#333', marginBottom: '15px' }}>Booking Policy</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea', marginBottom: '5px' }}>Cancellation</div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Free cancellation up to 24 hours before departure</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea', marginBottom: '5px' }}>Rescheduling</div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Reschedule 2 hours before departure (charges apply)</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea', marginBottom: '5px' }}>Boarding</div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Arrive at boarding point 30 minutes before departure</div>
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#667eea', marginBottom: '5px' }}>Refund</div>
              <div style={{ fontSize: '0.9rem', color: '#666' }}>Refunds processed within 5-7 working days</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Bus Management System</h4>
            <p style={{ color: '#cbd5e0', fontSize: '0.9rem' }}>
              Safe, comfortable, and reliable bus travel.
            </p>
          </div>
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Support</h4>
            <Link to="/help" style={styles.footerLink}>24/7 Customer Care</Link>
            <Link to="/faq" style={styles.footerLink}>FAQ</Link>
            <Link to="/contact" style={styles.footerLink}>Contact Us</Link>
          </div>
          <div style={styles.footerSection}>
            <h4 style={styles.footerTitle}>Legal</h4>
            <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
            <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
            <Link to="/cancellation" style={styles.footerLink}>Cancellation Policy</Link>
          </div>
        </div>
        <div style={styles.copyright}>
          © {new Date().getFullYear()} Bus Management System. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default BusSeatSelection;